import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useDeleteCommunityReplyMutation } from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { PostId } from '../model/post.types';
import { ReplyId } from '../model/reply.types';
import { modifyPostCommentCount } from '../lib/modifyPostCommentCount';

type DeleteReplyArgs = {
  communityId: CommunityId;
  postId: PostId | null;
  replyId: ReplyId;
};

export const useDeleteReply = () => {
  const [deleteReplyMutation] = useDeleteCommunityReplyMutation();

  return useCallback(
    ({ communityId, postId, replyId }: DeleteReplyArgs): Promise<boolean> =>
      deleteReplyMutation({
        variables: { communityId, replyId },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteReply: { __typename: 'Reply', id: replyId },
        },
        update: (cache) => {
          // cache.modify without an id would edit ROOT_QUERY
          if (postId === null) return;
          // The modifier runs once per replies(...) page variant and the same
          // reply can sit in several of them, so count removals by id.
          const removedIds = new Set<string>();
          cache.modify({
            id: cache.identify({ __typename: 'Post', id: postId }),
            fields: {
              replies: (existing: readonly Reference[] = [], { readField }) =>
                existing.filter((ref) => {
                  const id = readField<string>('id', ref);
                  const isRemoved =
                    id === replyId || readField('parentId', ref) === replyId;
                  if (isRemoved && id !== undefined) removedIds.add(id);
                  return !isRemoved;
                }),
            },
          });
          modifyPostCommentCount(cache, postId, -Math.max(removedIds.size, 1));
        },
      })
        .then(() => {
          toast.success('Your comment was successfully deleted!');
          return true;
        })
        .catch(() => {
          toast.error('Something went wrong');
          return false;
        }),
    [deleteReplyMutation],
  );
};
